import * as React from 'react';

import {
    StageSliderSectionModel,
    XfStageSlideModel
} from '../../../../../generated/core';
import {AlertBox} from '../../../../components/AlertBox';
import {AuthoringWrapper} from '../../../../components/AuthoringWrapper';
import {useTrackingService} from '../../../../context';
import {StageSlider} from '../../../../d6/components/stage-slider';
import {SlideProps} from '../../../../d6/components/stage-slider/components/Slide';

import {MapTo} from '../../../../infrastructure/compatibility/MapTo';
import {C} from '../../../../registries/compatibilty';

import {
    NavUiTrackingProps,
    useTrackNavUiClick
} from '../../../../utils/tracking/useTrackNavUiClick';
import {
    TrackSliderLoadArguments,
    useTrackSliderLoad
} from '../../../../utils/tracking/useTrackSliderLoad';
import {useAuthorHeightCalc} from '../../../../utils/useAuthorHeightCalc';
import {XfStageSlide} from '../../../structure/main/XfStageSlide';
import {SectionWrapper} from '../SectionWrapper';
import {
    AutoScrollTrackingProps,
    useHandleTrackingEvents
} from '../../../../hooks/useHandleTrackingEvents';
import {
    disclaimerActions,
    DisclaimerHeightProvider,
    useDisclaimerHeights
} from '../../../../d6/context/DisclaimerHeight';
import {useSectionVisibility} from '../../../../infrastructure/compatibility/useSectionVisibility';
import {parseHeadingRichtextToString} from '../../../../utils/parseHeadingRichtextToString';

const MIN_VALID_XF = 2;
const SLIDE_BULLET_LABEL = 'stage-slide-bullet-';
const SLIDE_PANEL_LABEL = 'stage-slide-panel-';

export interface StageSliderSectionModelExtended
    extends StageSliderSectionModel {
    cqItems: {
        [item: string]: XfStageSlideModel;
    };
}

export const InternalStageSliderSection = (
    props: StageSliderSectionModelExtended
) => {
    const {
        anchorId,
        autoScrollTimeout,
        contentId: sectionId,
        contentLabels,
        contentName,
        cqItems,
        cqPath,
        goToNextSlideLabel,
        goToPreviousSlideLabel,
        mboxId,
        numberOfValidElements,
        paginationAriaLabel,
        pauseAriaLabel,
        playAriaLabel,
        stageSliderAriaLabel,
        tagTypeBroken,
        validationError,
        validationMessages
    } = props;
    const {dispatch, disclaimerHeights} = useDisclaimerHeights();

    const {setVisible} = useSectionVisibility();
    const showMainViewSection = numberOfValidElements >= MIN_VALID_XF;

    React.useEffect(() => {
        // set visibility of section
        if (!C.isInEditor()) setVisible(showMainViewSection);
    }, [showMainViewSection, setVisible]);

    const handleDisclaimerHeight = React.useCallback(
        (payload: number) => {
            if (disclaimerHeights.includes(payload)) return;

            dispatch({
                type: disclaimerActions.addHeight,
                payload
            });
        },
        [disclaimerHeights, dispatch]
    );

    const handleResetDisclaimerHeights = React.useCallback(() => {
        dispatch({type: disclaimerActions.resetState});
    }, [dispatch]);

    const trackingService = useTrackingService();
    const trackSliderLoad = useTrackSliderLoad();
    const trackNavUiClick = useTrackNavUiClick();

    const handleLoadEvent = (props: TrackSliderLoadArguments) => {
        trackSliderLoad(props);
    };
    const handleManualEvent = (props: NavUiTrackingProps) => {
        trackNavUiClick(props);
    };
    const handleRotatingEvent = (props: AutoScrollTrackingProps) => {
        trackingService.trackSliderAutoScrollClick(props);
    };
    const handleTrackingEvents = useHandleTrackingEvents({
        contentLabels,
        cqPath,
        handleLoadEvent,
        handleManualEvent,
        handleRotatingEvent
    });

    const slidesList: SlideProps[] = Object.values(cqItems)
        .filter(({validXfStageSlide}) => validXfStageSlide)
        .map((xf, idx, slideList) => {
            const path = `item_${idx}`;
            const slidePanelName = `${SLIDE_PANEL_LABEL}${idx}`;
            const headingLabel =
                xf.slideTitle ||
                parseHeadingRichtextToString(xf.cqItems.heading.richtext) ||
                slidePanelName;

            return {
                slide: (
                    <XfStageSlide
                        clearDisclaimerHeights={handleResetDisclaimerHeights}
                        itemCount={slideList.length}
                        itemPosition={idx + 1}
                        key={path}
                        path={path}
                        reportDisclaimerHeight={handleDisclaimerHeight}
                    />
                ),
                id: slidePanelName,
                ariaLabelledBy: `${SLIDE_BULLET_LABEL}${idx}`,
                headingLabel,
                disclaimers: undefined,
                isActive: undefined
            };
        });

    const validationAlert = validationError && C.isInEditor() && (
        <AlertBox>
            {validationMessages.map((msg, idx) => (
                <span key={idx}>{msg}</span>
            ))}
        </AlertBox>
    );

    const mainView = (
        <SectionWrapper
            mboxId={mboxId}
            anchorId={anchorId}
            sectionId={sectionId}
            contentLabels={contentLabels}
            contentName={contentName}
        >
            {validationAlert}
            <StageSlider
                slides={slidesList}
                paginationAriaLabel={paginationAriaLabel}
                previousSlideAriaLabel={goToPreviousSlideLabel}
                nextSlideAriaLabel={goToNextSlideLabel}
                stageSliderAriaLabel={stageSliderAriaLabel}
                playAriaLabel={playAriaLabel}
                pauseAriaLabel={pauseAriaLabel}
                timeout={autoScrollTimeout}
                heightOverride={useAuthorHeightCalc(0.8)}
                isAuthorMode={C.isInEditor()}
                onTrackingEventSent={handleTrackingEvents}
            />
        </SectionWrapper>
    );

    const authorView = (
        <AuthoringWrapper
            anchorId={anchorId}
            title="S141 Stage Slider Section"
            bgColor={AuthoringWrapper.BG_COLOR_SECTION}
            tagTypeBroken={tagTypeBroken}
        >
            {mainView}
        </AuthoringWrapper>
    );

    const publishView = showMainViewSection ? mainView : <></>;

    return C.isInEditor() ? authorView : publishView;
};

const Component = (props: StageSliderSectionModelExtended) => (
    <DisclaimerHeightProvider>
        <InternalStageSliderSection {...props} />
    </DisclaimerHeightProvider>
);

export const StageSliderSection = MapTo(
    'vwa-ngw18/components/editorial/sections/stageSliderSection',
    Component,
    {noElementRecursively: true}
);
